import React, { useState, useEffect, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import cn from "classnames";
import styles from "./PartnerDetail.module.sass";
import { Link } from "react-router-dom";
import Icon from "../../components/Icon";

// Screens
import NameAndDescription from "./NameAndDescription";
import CandidateListView from "./Candidate&List";

// Components
import Card from "../../components/Card";
import Dropdown from "../../components/Dropdown";

// Services
import { getOfferById } from "../../services/offer.service";

// data
const navigation = ["Détails de l'offre", "Candidatures"];

// Fonction pour obtenir la classe de statut
const getStatusClass = (status) => {
  switch (status) {
    case "active":
      return "status-green";
    case "closed":
      return "status-red";
    case "draft":
      return "status-yellow";
    default:
      return "status-gray";
  }
};

// Fonction pour formater le statut
const formatStatus = (status) => {
  switch (status) {
    case "active":
      return "Active";
    case "closed":
      return "Fermée";
    case "draft":
      return "Brouillon";
    default:
      return status;
  }
};

const AlternanceOfferDetail = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true); // 👈 Ajout de l'état de chargement
  const [error, setError] = useState(null); // 👈 Ajout de l'état d'erreur
  const location = useLocation();
  const { id } = useParams();

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      return new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(date);
    } catch (e) {
      return dateString;
    }
  }, []);

  useEffect(() => {
    // Si les données sont déjà passées via l'état de la route, on les utilise directement
    if (location.state?.offer) {
      setOffer(location.state.offer);
      setLoading(false); // L'offre est déjà là, donc on n'est plus en chargement
      return;
    }

    // Sinon, on charge les données depuis l'API
    const loadOffer = async () => {
      try {
        setLoading(true); // Démarrage du chargement
        setError(null); // Réinitialisation des erreurs
        console.log("AlternanceOfferDetail - Chargement de l'offre avec ID:", id);
        const result = await getOfferById(id);

        console.log("coucou", result);

        if (result.success && result.data) {
          console.log("AlternanceOfferDetail - Données reçues:", result.data);
          setOffer(result.data);
        } else {
          console.error("Erreur lors de la récupération de l'offre:", result.error);
          setError(result.error || "Une erreur est survenue.");
        }
      } catch (err) {
        console.error("Erreur:", err);
        setError("Erreur lors de la récupération de l'offre.");
      } finally {
        setLoading(false); // Fin du chargement, que ce soit un succès ou un échec
      }
    };
    loadOffer();
  }, [id, location.state]);

  // Calculer le nombre de candidatures
  const candidatesCount = offer?.candidats_ids?.length || 0;

  // Affichage conditionnel basé sur l'état de chargement et d'erreur
  if (loading) {
    return (
      <div className={cn(styles.shop, styles.centerContent)}>
        <p>Chargement des détails de l'offre...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn(styles.shop, styles.centerContent)}>
        <p className={styles.errorMessage}>Erreur : {error}</p>
        <Link className={cn("button-white", styles.button)} to="/company/alternance-offers">
          <Icon name="arrow-left" />
          Retour
        </Link>
      </div>
    );
  }

  // Le reste du code ne s'exécute que si l'offre a été chargée
  return (
    <>
      <div className={styles.shop}>
        <Link className={cn("button-white", styles.button)} to="/company/alternance-offers">
          <Icon name="arrow-left" />
          Retour
        </Link>

        <div className={styles.background}>
          <img src="/images/content/bg-shop.jpg" alt="Background" />
        </div>

        <Card className={styles.card}>
          <div className={styles.profile}>
            <div className={styles.details}>
              <div className={styles.avatar}>
                <img src="/images/content/convention_logo.png" alt="Icône convention" />
              </div>
              <div className={styles.wrap}>
                <div className={cn("h4", styles.man)}>
                  {offer.title || "Titre non défini"}
                </div>
                <div className={styles.login}>
                  {offer.sector_activity && offer.job_location
                    ? `${offer.sector_activity} • ${offer.job_location}`
                    : offer.sector_activity || offer.job_location || "Secteur et lieu non définis"}
                </div>
                <div className={styles.date}>
                  Créée le {formatDate(offer.created)}
                </div>
              </div>
            </div>

            <div className={styles.statusContainer}>
              <div className={styles.statusGroup}>
                <div className={styles.login}>Statut</div>
                <div className={cn(styles.statusBadge, "status-type", getStatusClass(offer.status))}>
                  {formatStatus(offer.status)}
                </div>
              </div>
              <div className={styles.statusGroup}>
                <div className={styles.login}>Candidatures</div>
                <div className={cn(styles.statusBadge, "status-type", "status-blue-dark")}>
                  {candidatesCount} candidat{candidatesCount > 1 ? "s" : ""}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.control}>
            <div className={cn(styles.nav, styles.desktopNav)}>
              {navigation.map((x, index) => (
                <button
                  className={cn(styles.link, {
                    [styles.active]: index === activeIndex,
                  })}
                  onClick={() => setActiveIndex(index)}
                  key={index}
                >
                  {x}
                </button>
              ))}
            </div>

            <div className={styles.mobileNav}>
              <Dropdown
                className={styles.dropdown}
                value={navigation[activeIndex]}
                setValue={(value) => {
                  const index = navigation.findIndex((x) => x === value);
                  if (index !== -1) setActiveIndex(index);
                }}
                options={navigation}
              />
            </div>
          </div>
        </Card>

        <div className={styles.wrap}>
          {activeIndex === 0 && <NameAndDescription offer={offer} />}
          {activeIndex === 1 && <CandidateListView offer={offer} candidatesCount={candidatesCount} />}
        </div>
      </div>
    </>
  );
};

export default AlternanceOfferDetail;